<template>
    <!-- Standard BAA template builder (grok-baa-template-builder-2026-08-23).
         Mirrors the Dev Policy split builder: grouped questions on the LEFT,
         live document preview on the RIGHT; stacks cleanly on mobile. Answers
         are saved ONCE on the company template row (template_answers) and
         inherited by every vendor send. The Medcurity master body is frozen
         onto the template at save time — this page never edits legal text. -->
    <form method="POST" :action="routesData.submit" ref="form" @submit="onSubmit" class="btb-wrap">
        <input type="hidden" name="_token" :value="csrf">
        <input type="hidden" name="source_baa_id" :value="sourceData.id" v-if="!templateData">

        <div class="btb-top card">
            <div class="btb-toprow">
                <div class="btb-name">
                    <label class="btb-label" for="btb-name">Template name</label>
                    <input id="btb-name" class="btb-input" type="text" name="name" v-model="name" required maxlength="255" placeholder="e.g. Our Standard BAA">
                </div>
                <div class="btb-freq">
                    <label class="btb-label">Review frequency</label>
                    <div class="btb-freqopts" role="radiogroup" aria-label="Review frequency">
                        <label v-for="opt in freqOptions" :key="opt.value" class="btb-chip" :class="{sel: renewalFrequency === opt.value}">
                            <input type="radio" name="renewal_frequency" :value="opt.value" v-model="renewalFrequency" class="btb-hiddeninput">
                            {{ opt.label }}
                        </label>
                    </div>
                </div>
                <div class="btb-actions">
                    <label class="btb-default" v-if="!forceDefault">
                        <input type="checkbox" name="set_default" value="1" v-model="setDefault"> Set as my default
                    </label>
                    <input type="hidden" name="set_default" value="1" v-if="forceDefault">
                    <button type="submit" class="btb-btn">{{ templateData ? 'Save template' : 'Save standard BAA' }}</button>
                </div>
            </div>
            <div class="btb-progress" role="status" aria-live="polite">{{ answeredCount }} of {{ askableCount }} questions answered<span v-if="forceDefault"> · this will become your default template</span></div>
        </div>

        <div class="btb-split">
            <!-- LEFT: grouped questions -->
            <section class="card btb-questions">
                <div class="btb-qhead">
                    <span class="btb-qtitle">Your standard choices</span>
                    <span class="btb-qsub">Answer these once, and every BAA you send inherits them. Written-out answers start from standard language you can accept or edit.</span>
                </div>

                <div v-for="g in groups" :key="g.key" class="btb-group">
                    <div class="btb-gt">{{ g.title }}</div>
                    <div v-for="f in g.fields" :key="f.slug" class="btb-field" :class="{answered: isAnswered(f)}">
                        <div class="btb-flabel">{{ f.label }}</div>
                        <div class="btb-fhelp" v-if="f.help">{{ f.help }}</div>
                        <div v-if="f.checkbox" class="btb-options" role="group" :aria-label="f.label">
                            <label v-for="o in f.options" :key="o.value" class="btb-opt btb-check" :class="{sel: f.value.indexOf(o.value) !== -1}">
                                <input type="checkbox" class="btb-hiddeninput" :value="o.value" v-model="f.value" @change="sync(f)">
                                <span class="btb-mark btb-marksq" aria-hidden="true">&#10003;</span>
                                <span class="btb-otext">{{ o.label }}</span>
                            </label>
                        </div>
                        <div v-else-if="f.options && f.options.length" class="btb-options" role="radiogroup" :aria-label="f.label">
                            <label v-for="o in f.options" :key="o.value" class="btb-opt" :class="{sel: f.value === o.value}">
                                <input type="radio" class="btb-hiddeninput" :name="'btb-' + f.slug" :value="o.value" v-model="f.value" @change="sync(f)">
                                <span class="btb-mark" aria-hidden="true"></span>
                                <span class="btb-otext">{{ o.label }}</span>
                            </label>
                        </div>
                        <textarea v-else-if="f.multiline" class="btb-input btb-area" v-model="f.value" @input="sync(f)" :placeholder="f.label"></textarea>
                        <input v-else class="btb-input" type="text" v-model="f.value" @input="sync(f)" :placeholder="f.label">
                    </div>
                </div>

                <div class="btb-group" v-if="vendorFields.length">
                    <div class="btb-gt">Asked when you send</div>
                    <div class="btb-fhelp" style="margin-bottom:10px;">These are specific to each vendor and are filled in at send time — the vendor's legal name automatically, the dates defaulting to the send date (editable at review).</div>
                    <div v-for="f in vendorFields" :key="f.slug" class="btb-vendorfield">
                        <span class="btb-vdot" aria-hidden="true"></span>{{ f.label }}
                    </div>
                </div>

                <!-- answers travel as answers[slug] / answers[slug][] -->
                <template v-for="f in askableFields">
                    <template v-if="f.checkbox">
                        <input type="hidden" :name="'answers[' + f.slug + '][]'" v-for="(v,i) in f.value" :key="f.slug + '-' + i" :value="v">
                    </template>
                    <input type="hidden" v-else-if="String(f.value || '').trim() !== ''" :name="'answers[' + f.slug + ']'" :key="f.slug" :value="f.value">
                </template>
            </section>

            <!-- RIGHT: live document -->
            <section class="card btb-prevcard">
                <div class="btb-prevhead">
                    <span class="btb-pt">&#128196; {{ sourceData.name }}</span>
                    <span class="btb-live">Live preview</span>
                </div>
                <div class="btb-doc" ref="doc">
                    <div ref="tmpl" v-html="sourceData.with_fields"></div>
                </div>
            </section>
        </div>
    </form>
</template>

<script>
import { matchOptionValue, checkboxValuesFromStored } from '../baa/choiceMatching'
import { FIELD_COPY, groupFieldsByTheme, isVendorSpecificSlug, resolveFieldValue, applyPreviewTriggers, autoSizePreviewFields, mountInlineEditors, syncInlineEditors, bindPreviewEditing, applyPreviewReadOnly, mirrorFieldValue, isJunkAnswer } from '../baa/templateThemes'

export default {
    name: 'BaaTemplateBuilder',
    props: ['csrf', 'sourceData', 'templateData', 'routesData', 'isDefault', 'isFirstTemplate'],
    data(){
        return {
            name: (this.templateData && this.templateData.name) || 'Our Standard BAA',
            renewalFrequency: String((this.templateData && this.templateData.renewal_frequency) || '12'),
            setDefault: !!(this.isDefault || this.isFirstTemplate),
            fields: [],
            freqOptions: [
                { value: '12', label: 'Annual' },
                { value: '24', label: 'Every two years' },
                { value: '36', label: 'Every three years' },
            ],
        }
    },
    computed: {
        forceDefault(){ return !!this.isFirstTemplate },
        askableFields(){
            return this.fields.filter(function(f){ return !isVendorSpecificSlug(f.slug) })
        },
        vendorFields(){
            return this.fields.filter(function(f){ return isVendorSpecificSlug(f.slug) })
        },
        groups(){ return groupFieldsByTheme(this.askableFields) },
        askableCount(){ return this.askableFields.length },
        answeredCount(){
            var self = this
            return this.askableFields.filter(function(f){ return self.isAnswered(f) }).length
        },
    },
    mounted(){
        this.$nextTick(this.extract)
    },
    methods: {
        // Round 10 (Joe req 2026-09-01): an answer that says nothing ("none",
        // "n/a", "tbd", ...) counts as UNANSWERED here, so the "N of M answered"
        // progress line tells the truth before the admin saves rather than
        // after. The server drops the same values on save
        // (CompanyBaaTemplateController::validatedAnswers), so the standard
        // default applies to the field next time the builder opens.
        isAnswered(f){
            if(f.checkbox) return Array.isArray(f.value) && f.value.length > 0
            // Only free text can "say nothing" - a select whose own option is
            // literally "None" is a real answer and stays answered.
            if(f.options && f.options.length) return !!(f.value && String(f.value).trim())
            return !isJunkAnswer(f.value)
        },
        // Same extraction approach as the send wizard: the server renders the
        // template's own inputs (with_fields) and we lift each unique slug into
        // a question object whose edits reflect straight back into the preview.
        extract(){
            var root = this.$refs.tmpl
            this.fields = []
            if(!root) return
            var domEls = root.querySelectorAll('input, textarea, select')
            var self = this
            var bySlug = {}
            var order = []
            Array.prototype.forEach.call(domEls, function(el){
                var elName = el.getAttribute('name') || ''
                if(!elName) return
                var slug = elName.replace(/\[.*$/,'')
                if(!bySlug[slug]){ bySlug[slug] = []; order.push(slug) }
                bySlug[slug].push(el)
            })
            var saved = (this.templateData && this.templateData.answers) || {}
            if(Array.isArray(saved)) saved = {}
            order.forEach(function(slug){
                var els = bySlug[slug]
                var el = els[0]
                var copy = FIELD_COPY[slug] || null
                var label = (copy && copy.title) || el.getAttribute('placeholder') || self.humanize(slug)
                var help = (copy && copy.help) || ''
                var isCheckbox = (el.tagName === 'INPUT' && (el.type === 'checkbox' || (el.getAttribute('type') || '').toLowerCase() === 'checkbox'))
                if(isCheckbox){
                    var cbOptions = []
                    els.forEach(function(e){
                        var v = e.value
                        if(v === null || v === undefined) return
                        if(!cbOptions.some(function(o){ return o.value === v })){
                            cbOptions.push({ value: v, label: v })
                        }
                    })
                    var raw = Object.prototype.hasOwnProperty.call(saved, slug) ? saved[slug] : []
                    var pool = Array.isArray(raw) ? raw : (raw === null || raw === undefined || raw === '' ? [] : [raw])
                    var field = {
                        els: els, slug: slug, label: label, help: help,
                        options: cbOptions, checkbox: true, multiline: false,
                        value: checkboxValuesFromStored(cbOptions, pool),
                    }
                    self.fields.push(field)
                    self.reflect(field)
                    return
                }
                var options = null
                if(el.tagName === 'SELECT'){
                    options = []
                    Array.prototype.forEach.call(el.options, function(o){
                        if(o.value !== ''){ options.push({ value: o.value, label: (o.textContent || o.value).trim() }) }
                    })
                }
                // Round 9 (Joe req 2026-09-01): what this question opens with.
                // A saved answer still wins outright, so editing an existing
                // template never rewrites the admin's own words; a free-text
                // question with nothing saved and nothing rendered opens with
                // the standard default clause language instead of an empty box
                // whose only content was a placeholder repeating the question.
                // The rule itself lives in templateThemes.resolveFieldValue so
                // it is unit-testable without mounting this component.
                var hasSaved = Object.prototype.hasOwnProperty.call(saved, slug)
                var savedVal = hasSaved ? String(saved[slug]) : ''
                if(hasSaved && options && options.length){
                    var match = matchOptionValue(options, savedVal)
                    savedVal = (match === null || match === undefined) ? savedVal : match
                }
                var value = resolveFieldValue({
                    slug: slug,
                    domValue: el.value || '',
                    savedValue: savedVal,
                    hasSaved: hasSaved,
                    hasOptions: !!(options && options.length),
                })
                var fieldObj = {
                    els: els, slug: slug, label: label, help: help,
                    options: options, checkbox: false,
                    multiline: el.tagName === 'TEXTAREA',
                    value: value,
                }
                self.fields.push(fieldObj)
                self.reflect(fieldObj)
            })
            // Round 10: the preview's conditional blocks are server-rendered
            // hidden and are only revealed by the option's data-triggers
            // contract. Apply it now so a saved/default answer's clause is
            // visible the moment the builder opens, not just after a click.
            applyPreviewTriggers(root)
            // Round 11: size every revealed fill-in to its content, so the
            // clause reads as a sentence instead of a 119px peephole. After
            // the reveal, never before: a hidden control measures as zero.
            autoSizePreviewFields(root)
            // Round 12: give the sentence-continuation fill-ins an inline
            // editable shim so they wrap. Edits there are pushed back through
            // this component's normal sync, so answers[slug] posting and the
            // answered counter behave exactly as they did before.
            mountInlineEditors(root, { onInput: this.onInlineEdit })
            // Round 13: every OTHER preview control (the individual-rights
            // textareas, the clause selects, the checkbox groups, the
            // record-data inputs) syncs back too. Before this, nothing listened
            // to them at all, so the document accepted keystrokes it discarded.
            // Round 13b: lock the per-send record data FIRST, so the binding
            // below skips it. In this builder those fields have no editable
            // home at all - they render as the read-only "Asked when you send"
            // list and are excluded from the answers[] this form posts - so
            // accepting keystrokes for them would be accepting keystrokes we
            // throw away.
            applyPreviewReadOnly(root, this.isPreviewReadOnly)
            bindPreviewEditing(root, {
                onInput: this.onInlineEdit,
                getField: this.fieldBySlug,
            })
        },
        // Per-send record data (vendor legal name, agreement dates): answered at
        // send time, never on the template.
        isPreviewReadOnly(slug){
            return isVendorSpecificSlug(slug)
        },
        fieldBySlug(slug){
            return this.fields.find(function(f){ return f.slug === slug })
        },
        // Round 13b: one writer for every builder -> preview push, and it never
        // writes into the control that currently has focus. Without that guard a
        // keystroke in the question pane silently overwrote an in-progress edit
        // in the preview.
        reflect(f){
            if(!f.els) return
            mirrorFieldValue(f, f.value, (typeof document !== 'undefined') ? document.activeElement : null)
        },
        sync(f){
            this.reflect(f)
            // Round 10: reflect() assigns el.value directly, which fires no
            // change event, so the server's own trigger script would never run.
            // Re-apply the show/hide contract on every edit, so choosing "Yes"
            // reveals its clause and the free text lands in the document the
            // user is looking at, live, as it is typed.
            applyPreviewTriggers(this.$refs.tmpl)
            autoSizePreviewFields(this.$refs.tmpl)
            syncInlineEditors(this.$refs.tmpl)
            this.highlight(f)
        },
        // Round 12: an edit made in the document's inline shim. Route it through
        // the same field state a left-pane edit uses, so nothing downstream can
        // tell the difference.
        onInlineEdit(slug, value, sourceEl){
            var field = this.fieldBySlug(slug)
            if(!field) return
            field.value = value
            // Round 13: mirror into the field's OTHER elements, never back into
            // the one being typed in - rewriting a focused control's value
            // resets its caret.
            mirrorFieldValue(field, value, sourceEl)
            applyPreviewTriggers(this.$refs.tmpl)
            autoSizePreviewFields(this.$refs.tmpl)
            syncInlineEditors(this.$refs.tmpl)
        },
        highlight(f){
            var el = f.els && f.els[0]
            var doc = this.$refs.doc
            if(el && doc && el.getBoundingClientRect){
                var er = el.getBoundingClientRect(), dr = doc.getBoundingClientRect()
                doc.scrollTop += (er.top - dr.top) - (doc.clientHeight / 2) + (er.height / 2)
            }
        },
        humanize(slug){ return (slug||'').replace(/_/g,' ').replace(/\b\w/g, function(c){ return c.toUpperCase() }) },
        onSubmit(){
            // Plain POST — the hidden answers[...] inputs above carry the data.
            // Nothing here sends email or touches any vendor BAA.
            return true
        },
    },
}
</script>

<style scoped>
.btb-wrap{max-width:1280px;margin:0 auto;padding:6px 4px 48px;}
.card{background:#fff;border:1px solid #e5e7eb;border-radius:16px;box-shadow:0 1px 2px rgba(16,24,40,.05);}
.btb-top{padding:16px 20px 12px;margin-bottom:16px;}
.btb-toprow{display:flex;flex-wrap:wrap;gap:16px;align-items:flex-end;}
.btb-name{flex:1 1 260px;}
.btb-label{display:block;font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#0071BC;font-weight:800;margin-bottom:6px;}
.btb-input{width:100%;padding:11px 13px;border:1.5px solid #e5e7eb;border-radius:11px;font-size:.98rem;background:#fff;color:#1f2a37;}
.btb-input:focus{outline:none;border-color:#0071BC;box-shadow:0 0 0 3px rgba(0,113,188,.12);}
.btb-area{min-height:110px;font-family:inherit;line-height:1.5;}
.btb-freqopts{display:flex;gap:8px;flex-wrap:wrap;}
.btb-chip{border:1.5px solid #e5e7eb;border-radius:999px;padding:9px 16px;font-weight:700;color:#1f2a37;cursor:pointer;font-size:.88rem;background:#fff;}
.btb-chip.sel{border-color:#0071BC;background:#eff6ff;color:#0b2545;}
.btb-hiddeninput{position:absolute;opacity:0;width:1px;height:1px;margin:0;padding:0;}
.btb-actions{display:flex;align-items:center;gap:14px;}
.btb-default{font-size:.85rem;font-weight:600;color:#1f2a37;margin:0;cursor:pointer;}
.btb-btn{border:none;border-radius:11px;padding:12px 22px;font-size:.95rem;font-weight:700;cursor:pointer;background:#0b2545;color:#fff;}
.btb-progress{margin-top:10px;font-size:.8rem;font-weight:700;color:#15803d;}
.btb-split{display:grid;grid-template-columns:minmax(340px,5fr) minmax(400px,7fr);gap:18px;align-items:start;}
.btb-questions{padding:18px 20px 22px;}
.btb-qhead{margin-bottom:6px;}
.btb-qtitle{display:block;font-weight:800;color:#0b2545;font-size:1.05rem;}
.btb-qsub{display:block;color:#6b7280;font-size:.85rem;margin-top:2px;}
.btb-group{border-top:1px solid #eef1f4;padding:16px 0 4px;margin-top:10px;}
.btb-gt{font-size:.72rem;text-transform:uppercase;letter-spacing:.06em;color:#0071BC;font-weight:800;margin-bottom:12px;}
.btb-field{margin-bottom:18px;padding-left:10px;border-left:3px solid #e5e7eb;}
.btb-field.answered{border-left-color:#15803d;}
.btb-flabel{font-weight:700;color:#0b2545;font-size:.92rem;line-height:1.45;margin-bottom:4px;}
.btb-fhelp{color:#6b7280;font-size:.82rem;line-height:1.5;margin-bottom:8px;}
.btb-options{display:flex;flex-direction:column;gap:8px;}
.btb-opt{position:relative;display:flex;align-items:flex-start;gap:10px;min-height:40px;border:1.5px solid #e5e7eb;border-radius:11px;padding:10px 13px;cursor:pointer;background:#fff;font-weight:600;color:#1f2a37;line-height:1.45;font-size:.88rem;transition:.12s;}
.btb-opt:hover{border-color:#bfdbfe;background:#eff6ff;}
.btb-opt.sel{border-color:#0071BC;background:#eff6ff;box-shadow:0 0 0 3px rgba(0,113,188,.1);}
.btb-mark{width:16px;height:16px;border-radius:50%;border:2px solid #e5e7eb;flex:0 0 auto;margin-top:2px;}
.btb-opt.sel .btb-mark{border-color:#0071BC;background:#0071BC;box-shadow:inset 0 0 0 3px #fff;}
.btb-marksq{border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:.66rem;color:transparent;line-height:1;}
.btb-opt.sel .btb-marksq{border-color:#0071BC;background:#0071BC;color:#fff;box-shadow:none;}
.btb-otext{flex:1;white-space:normal;word-break:break-word;}
.btb-hiddeninput:focus-visible ~ .btb-mark{outline:2px solid #0071BC;outline-offset:2px;}
.btb-vendorfield{display:flex;align-items:center;gap:8px;color:#5a6b80;font-size:.86rem;font-weight:600;padding:6px 0;}
.btb-vdot{width:8px;height:8px;border-radius:50%;background:#c7d0dc;flex:0 0 auto;}
.btb-prevcard{position:sticky;top:12px;}
.btb-prevhead{display:flex;align-items:center;justify-content:space-between;padding:14px 22px;border-bottom:1px solid #e5e7eb;}
.btb-pt{font-weight:800;color:#0b2545;font-size:.95rem;}
.btb-live{font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#15803d;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:999px;padding:2px 9px;}
.btb-doc{padding:26px 34px 34px;font-size:.95rem;line-height:1.75;color:#374151;max-height:calc(100vh - 160px);overflow:auto;}
.btb-doc >>> h1{font-size:1.3rem;font-weight:700;text-align:center;margin:0 0 1rem;color:#0b2545;}
.btb-doc >>> h2{font-size:1.05rem;font-weight:700;margin:1.4rem 0 .4rem;color:#0b2545;}
/* Round 11 (Joe req 2026-09-01): the revealed fill-ins must be readable.
   policy-input.blade.php gives every `text` fill-in Bootstrap's .w-25
   (width:25% !important), which is about 119px in this pane, and textareas a
   fixed two rows. That server component is shared with company_policy/editor
   and PolicyMedcurityInputs.vue, so it is left alone and these overrides are
   confined to THIS preview pane by the .btb-doc >>> prefix on every rule.
   autoSizePreviewFields() supplies the per-content size; these rules let it
   take effect and keep the control looking like an editable spot in the
   document rather than a form widget. */
.btb-doc >>> input.form-control,
.btb-doc >>> input[type="text"],
.btb-doc >>> input[type="tel"]{width:auto !important;max-width:100%;min-width:12ch;display:inline-block;vertical-align:baseline;
    height:auto;padding:1px 6px;font-size:inherit;line-height:inherit;font-family:inherit;color:#0b2545;
    background:#f8fbff;border:0;border-bottom:1px dashed #9dc3e6;border-radius:3px;box-shadow:none;field-sizing:content;}
.btb-doc >>> textarea.form-control{width:100%;max-width:100%;min-height:3.2em;height:auto;overflow:hidden;resize:vertical;
    display:block;margin:4px 0;padding:6px 8px;font-size:inherit;line-height:inherit;font-family:inherit;color:#0b2545;
    background:#f8fbff;border:1px solid #dbe7f3;border-radius:6px;box-shadow:none;field-sizing:content;}
.btb-doc >>> input.form-control:focus,
.btb-doc >>> textarea.form-control:focus{outline:2px solid rgba(0,113,188,.35);outline-offset:1px;background:#fff;}
.btb-doc >>> select.form-control{width:auto !important;max-width:100%;display:inline-block;vertical-align:baseline;
    height:auto;padding:1px 22px 1px 6px;font-size:inherit;line-height:inherit;font-family:inherit;}
/* Round 12 (Joe req 2026-09-01): a sentence-continuation fill-in is an inline
   span, so it WRAPS across as many lines as the clause needs and reads as one
   continuous sentence with its lead-in. The real <input> stays in the DOM as
   the submit-path source of truth and is only visually hidden (never
   display:none, which applyPreviewTriggers owns on these ids). Scoped to this
   preview pane by the .btb-doc >>> prefix, exactly as the round-11 rules are. */
.btb-doc >>> .mc-inline-edit{display:inline;white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word;
    font:inherit;color:#0b2545;padding:0 2px;border-radius:2px;background:#f8fbff;
    border-bottom:1px dashed #9dc3e6;outline:none;cursor:text;}
.btb-doc >>> .mc-inline-edit:focus{outline:2px solid rgba(0,113,188,.35);outline-offset:1px;background:#fff;}
.btb-doc >>> .mc-inline-edit:empty::before{content:attr(data-placeholder);color:#9aa7b8;font-style:italic;}
.btb-doc >>> .mc-shim-source{position:absolute !important;width:1px !important;height:1px !important;
    padding:0 !important;margin:-1px !important;overflow:hidden !important;clip:rect(0 0 0 0) !important;
    white-space:nowrap !important;border:0 !important;}
/* Round 13b (verification fix 2026-09-01): a preview control with no editable
   home is visibly not editable, rather than silently ignoring keystrokes. Muted
   ink on a flat ground, no dashed "type here" underline, and a default cursor.
   Scoped to this preview pane by the .btb-doc >>> prefix, like every rule around it. */
.btb-doc >>> input.mc-preview-readonly,
.btb-doc >>> textarea.mc-preview-readonly{background:#f3f5f7;color:#5a6b80;border-bottom:1px solid #e0e5ea;
    cursor:default;font-style:normal;}
.btb-doc >>> input.mc-preview-readonly:focus,
.btb-doc >>> textarea.mc-preview-readonly:focus{outline:2px solid rgba(90,107,128,.25);outline-offset:1px;background:#f3f5f7;}
@media (max-width:980px){
    .btb-split{display:block;}
    .btb-prevcard{position:static;margin-top:18px;}
    .btb-doc{max-height:560px;}
}
</style>
